/**
 * S13 — Agent Detail (`/govern/agents/:id`).
 *
 * Full-page detail for one agent (governed or shadow). Sections:
 *
 *   Header  · name, id, status, risk pill, vendor + model
 *   Tabs    · Overview · Risk · Policies · Drift · Lineage · Versions · Audit
 *
 * Shadow agents render a narrower view: header + detection summary +
 * primary "Bring under governance" CTA.
 */
import React, { useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  AlertTriangle,
  ShieldCheck,
  Activity,
  Database,
  ClipboardList,
  History,
  ScrollText,
  ExternalLink,
  Plus,
  FileDown,
} from "lucide-react";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntity } from "@/lib/useEntity";
import { Flywheel } from "@/components/brand/Flywheel";
import { Sparkline } from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";
import { generateAgentDossier } from "@/lib/agentDossier";
import {
  RiskScorePill,
  DriftBadge,
  StatusPill,
  fmtAud,
  fmtPer1k,
} from "./components/AgentPrimitives";

const SUB_RISK_FIELDS = [
  { key: "dataSensitivity", label: "Data sensitivity", hint: "Customer PII / regulated data scope" },
  { key: "autonomy", label: "Autonomy", hint: "Suggest → Recommend → Act" },
  { key: "regulatoryExposure", label: "Regulatory exposure", hint: "APRA · ASIC · Privacy Act mapping" },
  { key: "drift", label: "Drift posture", hint: "Quality + latency + hallucination" },
];

const TABS = [
  { id: "overview", label: "Overview", icon: ShieldCheck },
  { id: "risk", label: "Risk", icon: AlertTriangle },
  { id: "policies", label: "Policies", icon: ClipboardList },
  { id: "drift", label: "Drift", icon: Activity },
  { id: "lineage", label: "Lineage", icon: Database },
  { id: "versions", label: "Versions", icon: History },
  { id: "audit", label: "Audit", icon: ScrollText },
];

export default function AgentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Phase 3b — fetch the single agent from the dedicated REST endpoint
  // instead of scanning the bundle's agents array. Related collections
  // (policies, audit, requests, opportunities) still come from the
  // bundle for now — they're used for the secondary tabs and a single
  // detail page calling 4 more endpoints would be wasteful.
  const { item: agent, loading, error } = useEntity(`/api/agents/${id}`);
  const { policies, auditEntries, requests, opportunities, tenant } = useTenantData();
  const [tab, setTab] = useState("overview");

  if (loading) {
    return <AgentDetailSkeleton />;
  }

  if (error || !agent) {
    return (
      <div className="px-6 lg:px-8 py-10 anim-fade-up" data-testid="agent-detail-missing">
        <p className="eyebrow">Govern · Agent Detail</p>
        <h1 className="text-h1 text-ink-primary mt-1">Agent not found</h1>
        <p className="text-ink-secondary mt-2">
          No agent matches <code className="font-mono">{id}</code>. It may have been retired or this is a Customer Mode tenant.
        </p>
        <button
          type="button"
          onClick={() => navigate("/govern/agents")}
          className="mt-5 inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium text-tralenex-700 hover:bg-tralenex-50"
          data-testid="agent-detail-back"
        >
          <ArrowLeft size={13} /> Back to Agent Inventory
        </button>
      </div>
    );
  }

  if (agent.agentType === "shadow") {
    return <ShadowDetail agent={agent} onBack={() => navigate("/govern/agents")} />;
  }

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="agent-detail">
      <div className="absolute -right-32 -top-16 pointer-events-none">
        <Flywheel size={360} opacity={0.04} />
      </div>

      <BackLink />
      <DetailHeader agent={agent} tenant={tenant} policies={policies} auditEntries={auditEntries} requests={requests} opportunities={opportunities} />

      <Tabs current={tab} setCurrent={setTab} />

      <div className="mt-5 space-y-5" data-testid={`agent-detail-tab-${tab}`}>
        {tab === "overview" && (
          <OverviewSection agent={agent} requests={requests} opportunities={opportunities} />
        )}
        {tab === "risk" && <RiskSection agent={agent} />}
        {tab === "policies" && <PoliciesSection agent={agent} policies={policies} />}
        {tab === "drift" && <DriftSection agent={agent} />}
        {tab === "lineage" && <LineageSection agent={agent} />}
        {tab === "versions" && <VersionsSection agent={agent} />}
        {tab === "audit" && <AuditSection agent={agent} auditEntries={auditEntries} />}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function BackLink() {
  return (
    <Link
      to="/govern/agents"
      className="inline-flex items-center gap-1.5 text-meta font-semibold text-tralenex-700 hover:text-tralenex-900 mb-2"
      data-testid="agent-detail-back"
    >
      <ArrowLeft size={13} /> Agent Inventory
    </Link>
  );
}

function DetailHeader({ agent, tenant, policies = [], auditEntries = [], requests = [], opportunities = [] }) {
  const [downloading, setDownloading] = React.useState(false);
  const linkedRequest = requests.find((r) => r.id === agent.linkedRequestId);
  const linkedOpportunity = opportunities.find((o) => o.id === agent.linkedOpportunityId);

  const onDossier = async () => {
    if (downloading) return;
    setDownloading(true);
    const t = toast.loading("Building dossier PDF…");
    try {
      const { filename, fingerprint } = await generateAgentDossier({
        agent,
        tenant,
        policies,
        auditEntries,
        opportunity: linkedOpportunity,
        request: linkedRequest,
      });
      toast.success(`Saved ${filename}`, {
        id: t,
        description: `SHA-256 ${fingerprint}`,
        duration: 6000,
      });
    } catch (e) {
      toast.error("Could not generate dossier", { id: t, description: e?.message || "Unknown error" });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="surface-card p-5 shadow-e1" data-testid="agent-detail-header">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="eyebrow">Govern · Agent Detail</p>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-meta text-ink-secondary tabular-nums" data-testid="agent-detail-id">
              {agent.id}
            </span>
            <StatusPill status={agent.status} />
            <span className="text-meta text-ink-disabled">
              {agent.vendorName} · {agent.model} · {agent.version}
            </span>
          </div>
          <h1 className="text-h1 text-ink-primary mt-1" data-testid="agent-detail-name">
            {agent.name}
          </h1>
          <p className="text-ink-secondary mt-1 max-w-3xl">{agent.purpose}</p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={onDossier}
            disabled={downloading}
            className={cn(
              "inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-semibold transition-colors shadow-e1",
              downloading
                ? "bg-inset text-ink-disabled cursor-wait"
                : "bg-tralenex-600 text-white hover:bg-tralenex-700",
            )}
            data-testid="agent-detail-dossier"
            title="Generate a signed PDF dossier for this agent"
          >
            <FileDown size={14} /> {downloading ? "Building…" : "Signed dossier"}
          </button>
          <RiskScorePill score={agent.riskScore} band={agent.riskBand} size="lg" />
          <div className="text-right">
            <p className="text-meta text-ink-disabled uppercase tracking-[0.06em]">Composite risk</p>
            <p className="text-meta text-ink-secondary">
              Owner <span className="text-ink-primary font-semibold">{agent.ownerName}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border-subtle grid grid-cols-2 md:grid-cols-6 gap-3 text-meta">
        <Stat label="Department" value={agent.department} />
        <Stat label="Autonomy" value={agent.autonomy} />
        <Stat label="Framework" value={agent.framework} />
        <Stat label="Spend / mo" value={fmtAud(agent.monthlyCostAud)} mono />
        <Stat label="$ / 1k tok" value={fmtPer1k(agent.per1kToken)} mono />
        <Stat label="Value / mo" value={fmtAud(agent.monthlyValueAud)} mono accent="up" />
      </div>
    </div>
  );
}

function Stat({ label, value, mono, accent }) {
  return (
    <div>
      <p className="eyebrow text-ink-disabled">{label}</p>
      <p className={cn(
        "mt-0.5 text-sm text-ink-primary",
        mono && "tabular-nums font-mono",
        accent === "up" && "text-risk-low font-semibold",
      )}>{value}</p>
    </div>
  );
}

function Tabs({ current, setCurrent }) {
  return (
    <div className="mt-5 flex items-center gap-0.5 border-b border-border-subtle overflow-x-auto" data-testid="agent-detail-tabs">
      {TABS.map(({ id, label, icon: Icon }) => {
        const active = current === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setCurrent(id)}
            data-testid={`agent-detail-tab-${id}-btn`}
            aria-pressed={active}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 h-9 border-b-2 -mb-px transition-colors whitespace-nowrap",
              active
                ? "border-tralenex-600 text-ink-primary"
                : "border-transparent text-ink-secondary hover:text-ink-primary",
            )}
          >
            <Icon size={12} />
            <span className="text-sm font-medium">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Sections
// ───────────────────────────────────────────────────────────────────────────
function OverviewSection({ agent, requests, opportunities }) {
  const linkedRequest = agent.linkedRequestId ? requests.find((r) => r.id === agent.linkedRequestId) : null;
  const linkedOpp = agent.linkedOpportunityId ? opportunities.find((o) => o.id === agent.linkedOpportunityId) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <BusinessContextCard agent={agent} />
        <LinkedDeploymentCard agent={agent} linkedRequest={linkedRequest} linkedOpp={linkedOpp} />
      </div>
      <div className="space-y-5">
        <AtAGlanceCard agent={agent} />
        <MonthlyEconomicsCard agent={agent} />
      </div>
    </div>
  );
}

function BusinessContextCard({ agent }) {
  return (
    <Card title="Business context" testId="agent-overview-business">
      <p className="text-sm text-ink-primary">{agent.purpose}</p>
      <div className="mt-3 grid grid-cols-2 gap-3 text-meta">
        <Stat label="Department" value={agent.department} />
        <Stat label="Owner" value={agent.ownerName} />
        <Stat label="Deployed" value={agent.deployedAt} />
        <Stat label="Current version" value={agent.version} />
      </div>
    </Card>
  );
}

function LinkedDeploymentCard({ linkedRequest, linkedOpp }) {
  return (
    <Card title="Linked deployment" testId="agent-overview-deployment">
      {linkedRequest || linkedOpp ? (
        <div className="space-y-2.5">
          {linkedOpp && (
            <LinkRow
              label="Originating opportunity"
              id={linkedOpp.id}
              title={linkedOpp.title}
              to={`/identify/opportunities?opp=${linkedOpp.id}`}
              testId="agent-overview-link-opp"
            />
          )}
          {linkedRequest && (
            <LinkRow
              label="Deployment request"
              id={linkedRequest.id}
              title={`Stage ${linkedRequest.stage}/7 · ${linkedRequest.status}`}
              to={`/deploy/requests?req=${linkedRequest.id}`}
              testId="agent-overview-link-deploy"
            />
          )}
        </div>
      ) : (
        <p className="text-meta text-ink-secondary">
          No deployment request on file — this agent predates ALGOS governance.
        </p>
      )}
    </Card>
  );
}

function AtAGlanceCard({ agent }) {
  return (
    <Card title="At a glance" testId="agent-overview-glance">
      <div className="space-y-2.5">
        <GlanceRow label="Risk score" value={<RiskScorePill score={agent.riskScore} band={agent.riskBand} />} />
        <GlanceRow label="Status" value={<StatusPill status={agent.status} />} />
        <GlanceRow label="Drift signal" value={<DriftBadge signal={agent.driftSignal} />} />
        <GlanceRow label="Open incidents" value={<span className="tabular-nums">{agent.incidentsOpen}</span>} />
        <GlanceRow label="Policies applied" value={<span className="tabular-nums">{agent.policiesApplied}</span>} />
        <GlanceRow label="Framework" value={<span className="text-sm text-ink-primary">{agent.framework}</span>} />
      </div>
    </Card>
  );
}

function MonthlyEconomicsCard({ agent }) {
  const multiple = (agent.monthlyValueAud / Math.max(1, agent.monthlyCostAud)).toFixed(1);
  return (
    <Card title="Monthly economics" testId="agent-overview-economics">
      <div className="grid grid-cols-2 gap-3 text-center">
        <div>
          <p className="eyebrow text-ink-disabled">Spend</p>
          <p className="text-h3 text-ink-primary tabular-nums" data-testid="agent-econ-spend">{fmtAud(agent.monthlyCostAud)}</p>
        </div>
        <div>
          <p className="eyebrow text-ink-disabled">Value</p>
          <p className="text-h3 text-risk-low tabular-nums">{fmtAud(agent.monthlyValueAud)}</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-border-subtle grid grid-cols-2 gap-3 text-center">
        <div>
          <p className="eyebrow text-ink-disabled">Return</p>
          <p className="text-display-md text-tralenex-700 tabular-nums">{multiple}×</p>
        </div>
        <div>
          <p className="eyebrow text-ink-disabled">$ / 1k tok</p>
          <p className="text-display-md text-ink-primary tabular-nums font-mono" data-testid="agent-econ-per1k">{fmtPer1k(agent.per1kToken)}</p>
        </div>
      </div>
    </Card>
  );
}

function RiskSection({ agent }) {
  return (
    <Card title="Composite risk breakdown" testId="agent-risk-card">
      <p className="text-meta text-ink-secondary mb-4">
        Composite risk is computed from four weighted dimensions. Hover any
        bar to see the source signal. High-risk agents (≥70) require quarterly
        CAIO + Legal sign-off.
      </p>
      <div className="space-y-4" data-testid="agent-risk-breakdown">
        {SUB_RISK_FIELDS.map(({ key, label, hint }) => {
          const score = agent.subRiskScores?.[key] ?? 0;
          const band = score < 45 ? "low" : score < 70 ? "med" : "high";
          const barCls = band === "high" ? "bg-risk-high" : band === "med" ? "bg-risk-med" : "bg-risk-low";
          return (
            <div key={key} data-testid={`agent-risk-sub-${key}`}>
              <div className="flex items-baseline justify-between mb-1">
                <p className="text-sm font-semibold text-ink-primary">{label}</p>
                <p className={cn("text-meta font-bold tabular-nums", band === "high" ? "text-risk-high" : band === "med" ? "text-risk-med" : "text-risk-low")}>
                  {score} / 100
                </p>
              </div>
              <div className="h-1.5 w-full rounded-full bg-inset overflow-hidden">
                <div className={cn("h-full rounded-full transition-all", barCls)} style={{ width: `${score}%` }} />
              </div>
              <p className="text-meta text-ink-disabled mt-1">{hint}</p>
            </div>
          );
        })}
      </div>
      <div className="mt-5 pt-4 border-t border-border-subtle flex items-center justify-between">
        <div>
          <p className="eyebrow text-ink-disabled">Composite</p>
          <p className="text-display-md text-ink-primary tabular-nums">{agent.riskScore} / 100</p>
        </div>
        <RiskScorePill score={agent.riskScore} band={agent.riskBand} size="lg" />
      </div>
    </Card>
  );
}

function PoliciesSection({ agent, policies }) {
  const applied = (agent.policyIds || []).map((pid) => policies.find((p) => p.id === pid)).filter(Boolean);
  return (
    <Card title={`Applied policies · ${applied.length}`} testId="agent-policies-card">
      {applied.length === 0 ? (
        <p className="text-meta text-ink-secondary">
          No policies applied. This agent is operating outside the governance baseline.
        </p>
      ) : (
        <ol className="divide-y divide-border-subtle">
          {applied.map((p) => (
            <li key={p.id} className="py-2.5 flex items-start gap-3" data-testid={`agent-policy-${p.id}`}>
              <span className="mt-0.5 inline-flex items-center justify-center h-7 w-7 rounded bg-tralenex-50 text-tralenex-700 text-meta font-bold">
                {p.id.split("-")[1]}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink-primary">{p.name}</p>
                <p className="text-meta text-ink-secondary">
                  {p.category} · owner {p.owner} · {p.status} · last updated {p.lastUpdated}
                </p>
              </div>
              <span className={cn(
                "text-meta font-bold uppercase tracking-[0.06em] tabular-nums",
                p.violations > 0 ? "text-risk-high" : "text-risk-low",
              )}>
                {p.violations} viol.
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function DriftSection({ agent }) {
  const hist = agent.driftHistory || { quality: [], latencyMs: [], hallucinationRate: [] };
  const current = (arr) => (arr.length ? arr[arr.length - 1] : 0);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="agent-drift-section">
      <DriftCard
        title="Answer quality"
        unit="%"
        value={current(hist.quality)}
        good="up"
        data={hist.quality}
        testId="drift-quality"
      />
      <DriftCard
        title="P95 latency"
        unit=" ms"
        value={current(hist.latencyMs)}
        good="down"
        data={hist.latencyMs}
        stroke="#C49A00"
        testId="drift-latency"
      />
      <DriftCard
        title="Hallucination rate"
        unit="%"
        value={current(hist.hallucinationRate)}
        good="down"
        data={hist.hallucinationRate}
        stroke="#C8102E"
        testId="drift-hallucination"
      />
    </div>
  );
}

function DriftCard({ title, unit, value, data, stroke, testId }) {
  return (
    <div className="surface-card p-4 shadow-e1" data-testid={testId}>
      <p className="eyebrow">{title}</p>
      <p className="mt-1 text-display-md text-ink-primary tabular-nums">{value}{unit}</p>
      <p className="mt-0.5 text-meta text-ink-secondary">12-week trend</p>
      <div className="mt-2">
        <Sparkline data={data} stroke={stroke} height={42} width={260} />
      </div>
    </div>
  );
}

function LineageSection({ agent }) {
  const sources = agent.dataSources || [];
  return (
    <Card title="Data lineage" testId="agent-lineage-card">
      <div className="grid grid-cols-1 md:grid-cols-[1.2fr_auto_1fr_auto_1fr] gap-4 items-center" data-testid="agent-lineage-flow">
        <div>
          <p className="eyebrow">Data sources</p>
          <ul className="mt-2 space-y-1.5">
            {sources.map((s) => (
              <li key={s} className="text-sm text-ink-primary flex items-center gap-2">
                <Database size={12} className="text-ink-secondary" /> {s}
              </li>
            ))}
          </ul>
        </div>
        <div className="hidden md:flex items-center justify-center text-ink-disabled">→</div>
        <div>
          <p className="eyebrow">Model</p>
          <p className="mt-2 text-sm font-semibold text-ink-primary">{agent.vendorName}</p>
          <p className="text-meta text-ink-secondary font-mono">{agent.model} · {agent.version}</p>
          <p className="text-meta text-ink-disabled mt-1">Autonomy · {agent.autonomy}</p>
        </div>
        <div className="hidden md:flex items-center justify-center text-ink-disabled">→</div>
        <div>
          <p className="eyebrow">Consumers</p>
          <p className="mt-2 text-sm text-ink-primary">{agent.department}</p>
          <p className="text-meta text-ink-secondary">Owner · {agent.ownerName}</p>
          <p className="text-meta text-ink-disabled mt-1">Framework · {agent.framework}</p>
        </div>
      </div>
    </Card>
  );
}

function VersionsSection({ agent }) {
  return (
    <Card title="Version history" testId="agent-versions-card">
      <ol className="border-l border-border-subtle ml-2 space-y-4 pl-5">
        {(agent.versionHistory || []).map((v, i) => (
          <li key={`${v.version}-${i}`} className="relative" data-testid={`agent-version-${i}`}>
            <span className={cn(
              "absolute -left-[26px] top-1 h-3 w-3 rounded-full ring-2 ring-raised",
              i === 0 ? "bg-tralenex-600" : "bg-border-subtle",
            )} />
            <div className="flex items-baseline gap-2">
              <p className="text-sm font-mono text-ink-primary tabular-nums">{v.version}</p>
              {i === 0 && (
                <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-tralenex-700 bg-tralenex-50 px-1.5 py-0.5 rounded">
                  current
                </span>
              )}
              <span className="text-meta text-ink-disabled tabular-nums">{v.releasedAt}</span>
            </div>
            <p className="text-meta text-ink-secondary mt-0.5">{v.change}</p>
            <p className="text-meta text-ink-disabled mt-0.5">by {v.actor}</p>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function AuditSection({ agent, auditEntries }) {
  const matches = (auditEntries || []).filter((e) => (e.target || "").includes(agent.id)).slice(0, 25);
  return (
    <Card title={`Recent audit · ${matches.length} entries`} testId="agent-audit-card">
      {matches.length === 0 ? (
        <p className="text-meta text-ink-secondary">
          No audit entries reference this agent in the current window.
        </p>
      ) : (
        <ol className="divide-y divide-border-subtle">
          {matches.map((e, i) => (
            <li key={`${e.ts}-${e.actor}-${e.action}`} className="py-2 flex items-baseline justify-between gap-3" data-testid={`agent-audit-${i}`}>
              <div className="min-w-0">
                <p className="text-sm text-ink-primary">
                  <span className="font-semibold">{e.actor}</span> {e.action}
                </p>
                <p className="text-meta text-ink-disabled font-mono tabular-nums">{e.target}</p>
              </div>
              <p className="text-meta text-ink-disabled tabular-nums shrink-0">{e.ts}</p>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Shadow agent path
// ───────────────────────────────────────────────────────────────────────────
function ShadowDetail({ agent, onBack }) {
  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="agent-detail-shadow">
      <div className="absolute -right-32 -top-16 pointer-events-none">
        <Flywheel size={320} opacity={0.04} />
      </div>

      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-meta font-semibold text-tralenex-700 hover:text-tralenex-900 mb-2"
        data-testid="agent-detail-back"
      >
        <ArrowLeft size={13} /> Agent Inventory
      </button>

      <ShadowHeaderCard agent={agent} />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <ShadowDetectionCard agent={agent} />
          <ShadowImpactCard />
        </div>
        <div>
          <ShadowGovernanceCta />
        </div>
      </div>
    </div>
  );
}

function ShadowHeaderCard({ agent }) {
  return (
    <div className="surface-card p-5 shadow-e1 border-l-[3px] border-l-shadow-red">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="eyebrow text-shadow-red">Govern · Shadow AI</p>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-meta text-ink-secondary tabular-nums">{agent.id}</span>
            <StatusPill status={agent.status} />
          </div>
          <h1 className="text-h1 text-ink-primary mt-1">{agent.name}</h1>
          <p className="text-ink-secondary mt-1">{agent.purpose}</p>
        </div>
        <RiskScorePill score={agent.riskScore} band="shadow" size="lg" />
      </div>

      <div className="mt-4 pt-3 border-t border-border-subtle grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Department" value={agent.department} />
        <Stat label="Vendor" value={agent.vendorName} />
        <Stat label="Users detected" value={agent.usersDetected} mono />
        <Stat label="First seen" value={agent.firstSeen} />
      </div>
    </div>
  );
}

function ShadowDetectionCard({ agent }) {
  return (
    <Card title="Detection signal" testId="shadow-detection-card">
      <p className="text-sm text-ink-primary">{agent.detectedVia}</p>
      <p className="mt-2 text-meta text-ink-secondary">
        {agent.evidenceCount} evidence events captured across the last 30 days. Open the Evidence Locker to review SIEM correlation, DLP hits and SSO bypass attempts.
      </p>
    </Card>
  );
}

const SHADOW_IMPACT_BULLETS = [
  "· No ALGOS policy enforcement → data exfiltration risk.",
  "· No audit trail of inputs/outputs → regulator exposure.",
  "· Consumer plan ≠ enterprise contract → IP and confidentiality risk.",
  "· No drift / quality observability → silent degradation.",
];

function ShadowImpactCard() {
  return (
    <Card title="Why this matters" testId="shadow-impact-card">
      <ul className="space-y-2 text-sm text-ink-primary">
        {SHADOW_IMPACT_BULLETS.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </Card>
  );
}

function ShadowGovernanceCta() {
  return (
    <div className="surface-card p-4 shadow-e1 sticky top-4" data-testid="shadow-cta-card">
      <p className="eyebrow text-shadow-red">Recommended action</p>
      <h3 className="text-h3 text-ink-primary mt-1">Bring under governance</h3>
      <p className="text-meta text-ink-secondary mt-1">
        Start a deployment request to retro-fit this tool onto an
        approved vendor contract with the right policy baseline.
      </p>
      <button
        type="button"
        className="mt-3 w-full inline-flex items-center justify-center gap-2 h-10 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
        data-testid="shadow-bring-governance"
      >
        <Plus size={13} /> Start deployment request
      </button>
      <Link
        to="/govern/agents"
        className="mt-2 w-full inline-flex items-center justify-center gap-1 h-9 rounded-md text-meta text-ink-secondary hover:bg-inset"
      >
        <ArrowLeft size={11} /> Back to inventory
      </Link>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Card({ title, children, testId }) {
  return (
    <div className="surface-card shadow-e1" data-testid={testId}>
      <div className="px-5 pt-4 pb-3 border-b border-border-subtle">
        <p className="text-h3 text-ink-primary">{title}</p>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function LinkRow({ label, id, title, to, testId }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-3 rounded-md border border-border-subtle px-3 py-2.5 hover:border-tralenex-300 hover:bg-tralenex-50/40 transition-colors"
      data-testid={testId}
    >
      <div className="min-w-0">
        <p className="text-meta text-ink-disabled uppercase tracking-[0.06em]">{label}</p>
        <p className="text-sm font-semibold text-ink-primary truncate">{title}</p>
        <p className="font-mono text-meta text-ink-secondary tabular-nums">{id}</p>
      </div>
      <ExternalLink size={13} className="text-ink-secondary shrink-0" />
    </Link>
  );
}

function GlanceRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-meta text-ink-secondary uppercase tracking-[0.04em]">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

// Phase 3b loading skeleton (REST detail fetch).
function AgentDetailSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="agent-detail-loading">
      <div className="h-4 w-32 rounded-md bg-inset/50 animate-pulse" />
      <div className="mt-3 h-8 w-96 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-72 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 h-12 w-full rounded-md surface-card animate-pulse bg-inset/20" />
      <div className="mt-4 h-[480px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

